import {
    CollectionProvider,
    CollectionState,
    useCollectionContext,
} from 'components/settings/collectionContext';
import { Upload, uploadConverter, UPLOAD_COLLECTION_NAME } from 'models';
import { MouseEvent, useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { ref, uploadBytes } from 'firebase/storage';
import { Document, Page, pdfjs } from 'react-pdf';
import { db, storage, useAuthState } from 'firebaseStore';
import { addDoc, collection, updateDoc } from 'firebase/firestore';
import { NavLink, useParams } from 'react-router-dom';
import {
    DocState,
    DocumentProvider,
    useDocumentContext,
} from 'components/settings/documentContext';
import { usePathUrl } from 'components/usersPage/usersPage';
import { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist/types/src/display/api';

import './uploadPage.css';
import { PAGE_1 } from './page_1';
import { itemStyles } from 'components/contract/segment';

// https://github.com/wojtekmaj/react-pdf/issues/680#issuecomment-723656449 :
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

export const UploadPage = () => {
    const { uploadId } = useParams();
    const [page, setPage] = useState<PDFPageProxy>();
    const [selection, setSelection] = useState<string>();

    return (
        <div style={{ display: 'flex', height: 'calc(100% - 68px)' }}>
            <CollectionProvider collectionName={UPLOAD_COLLECTION_NAME} converter={uploadConverter}>
                <UploadsSection />
            </CollectionProvider>
            <DocumentProvider
                collectionName={UPLOAD_COLLECTION_NAME}
                converter={uploadConverter}
                id={uploadId || ''}
            >
                <UploadSection page={page} setPage={setPage} />
            </DocumentProvider>
            <TargetSection page={page} selection={selection} setSelection={setSelection} />
        </div>
    );
};

const UPLOADS_WIDTH = '240px';

export const UploadsSection = () => {
    const [uploads] = useCollectionContext(UPLOAD_COLLECTION_NAME) as CollectionState<Upload>;
    console.log('uploads', uploads);
    const [user, , tenant] = useAuthState();
    const collectionRef = collection(db, UPLOAD_COLLECTION_NAME).withConverter(uploadConverter);
    const onDrop = useCallback(
        async (acceptedFiles?: File[]) => {
            if (!acceptedFiles) {
                throw new Error('No file selected');
            }
            if (acceptedFiles.length === 0) {
                throw new Error('Cannot handle multiple files');
            }
            const selectedFile = acceptedFiles[0];
            if (selectedFile.type !== 'application/pdf') {
                throw new Error('Can only handle PDFs');
            }
            const fileType = encodeURIComponent(selectedFile.type);
            const fileName = encodeURIComponent(selectedFile.name);
            const newUpload = new Upload({
                createdBy: user?.email,
                uploadFilename: selectedFile.name,
                tenantId: tenant,
                storage: {},
            });

            return addDoc(collectionRef, newUpload).then(
                async (newDocRef) => {
                    const path = `${newUpload.tenantId}::${newDocRef.id}::${fileType}::${fileName}::v0`;
                    const storageRef = ref(storage, path);
                    await uploadBytes(storageRef, selectedFile);
                    newUpload.storage = { path, fileType, version: 'v0' };
                    return updateDoc(
                        newDocRef,
                        new Upload({ ...newUpload, id: newDocRef.id }).toFirestore()
                    );
                },
                (error) => {
                    console.error('addDoc error', error);
                }
            );
        },
        [collectionRef, tenant, user?.email]
    );
    const { getRootProps, getInputProps } = useDropzone({ onDrop });
    return (
        <div
            style={{
                height: '100%',
                backgroundColor: '#FCFBFE',
                width: UPLOADS_WIDTH,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
        >
            <div>Uploaded documents</div>
            <div
                {...getRootProps()}
                style={{
                    width: 'calc(100% - 20px)',
                    backgroundColor: '#18A0FB',
                    height: '48px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <input {...getInputProps()} />
                Upload
            </div>
            {uploads.map((upload) => {
                return (
                    <NavLink key={upload.id} to={`/uploads/${upload.id}`}>
                        <div>{upload.uploadFilename || upload.name}</div>
                    </NavLink>
                );
            })}
        </div>
    );
};

const E = 4;

export const UploadSection = ({
    page,
    setPage,
}: {
    page?: PDFPageProxy;
    setPage: (_: PDFPageProxy | undefined) => void;
}) => {
    const { uploadId } = useParams();
    const [numPages, setNumPages] = useState<number | null>(null);
    const [pageNumber] = useState(1);
    const [selected, setSelected] = useState('');
    async function onDocumentLoadSuccess(e: PDFDocumentProxy) {
        console.log('onDocumentLoadSuccess', e);
        const page1 = await e.getPage(pageNumber);
        setPage(page1);
        setNumPages(e.numPages);
    }
    const [, , width, height] = page?.view || [0, 0, 10, 10];
    const [upload, pending] = useDocumentContext(
        UPLOAD_COLLECTION_NAME,
        uploadId || ''
    ) as DocState<Upload>;
    const url = usePathUrl(upload?.storage.path || '');
    if (pending) {
        return <div>Loading</div>;
    }
    if (!upload) {
        return <div>Loading</div>;
    }
    if (!url) {
        return <div>Loading url</div>;
    }
    return (
        <div
            style={{
                height: '100%',
                flexGrow: 1,
                flexBasis: '400px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
        >
            <div>{upload.uploadFilename}</div>
            <div style={{ position: 'relative', border: '2px solid black' }}>
                {PAGE_1.blocks.map((block, i) => {
                    const blockId = `block-${i}`;
                    return [
                        <div
                            key={blockId}
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelected(blockId);
                            }}
                            style={{
                                position: 'absolute',
                                border: '2px solid black',
                                backgroundColor: `rgb(175 175 175 / ${
                                    selected === blockId ? 33 : 0
                                }%)`,
                                width: `${
                                    (block.boundingBox.normalizedVertices[1].x -
                                        block.boundingBox.normalizedVertices[0].x) *
                                        width +
                                    E * 2
                                }px`,
                                height: `${
                                    (block.boundingBox.normalizedVertices[2].y -
                                        block.boundingBox.normalizedVertices[0].y) *
                                        height +
                                    E * 2
                                }px`,
                                left: block.boundingBox.normalizedVertices[0].x * width - E,
                                top: block.boundingBox.normalizedVertices[0].y * height - E,
                                zIndex: 5,
                            }}
                        ></div>,
                        ...(block.paragraphs.length > 1
                            ? block.paragraphs.map((paragraph, j) => {
                                  const paragraphId = `block-${i}-paragraph-${j}`;
                                  return (
                                      <div
                                          key={paragraphId}
                                          onClick={(e) => {
                                              e.stopPropagation();
                                              setSelected(paragraphId);
                                          }}
                                          style={{
                                              position: 'absolute',
                                              border: '2px solid blue',
                                              backgroundColor: `rgb(24 160 251 / ${
                                                  selected === paragraphId ? 33 : 0
                                              }%)`,
                                              width: `${
                                                  (paragraph.boundingBox.normalizedVertices[1].x -
                                                      paragraph.boundingBox.normalizedVertices[0]
                                                          .x) *
                                                  width
                                              }px`,
                                              height: `${
                                                  (paragraph.boundingBox.normalizedVertices[2].y -
                                                      paragraph.boundingBox.normalizedVertices[0]
                                                          .y) *
                                                  height
                                              }px`,
                                              left:
                                                  paragraph.boundingBox.normalizedVertices[0].x *
                                                  width,
                                              top:
                                                  paragraph.boundingBox.normalizedVertices[0].y *
                                                  height,
                                              zIndex: 10,
                                          }}
                                      ></div>
                                  );
                              })
                            : []),
                    ];
                })}
                <div></div>
                <Document
                    file={url || '/gpc-travel-insurance-2021.pdf0'}
                    onLoadSuccess={onDocumentLoadSuccess}
                >
                    <Page pageNumber={pageNumber} />
                </Document>
            </div>
            <div>
                Page {pageNumber} of {numPages}
            </div>
        </div>
    );
};

export const TargetSection = ({
    page,
    selection,
    setSelection,
}: {
    page?: PDFPageProxy;
    selection?: string;
    setSelection: (_?: string) => void;
}) => {
    const onClick = (blockId: string) => (e: MouseEvent<HTMLDivElement>) => {
        if (blockId === selection) {
            setSelection(undefined);
            return;
        }
        setSelection(blockId);
    };
    if (!page) {
        return null;
    }
    return (
        <div
            style={{
                height: '100%',
                flexGrow: 1,
                flexBasis: '400px',
                backgroundColor: '#FCFBFE',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
        >
            <div style={{ width: '100%' }}>
                {PAGE_1.blocks.map((block, i) => {
                    const blockId = `block-${i}`;
                    console.log('block', block);
                    console.log('block.paragraphs.length', block.paragraphs.length);
                    return [
                        <div
                            onClick={onClick(blockId)}
                            key={blockId}
                            className="segmentItem"
                            style={itemStyles(0, '#F4F4F4')}
                        >
                            {block.paragraphs.length > 1
                                ? ''
                                : block.paragraphs
                                      .map((paragraph) =>
                                          paragraph.words
                                              .map((word) =>
                                                  word.symbols.map((symbol) => symbol.text).join('')
                                              )
                                              .join(' ')
                                      )
                                      .join('\n\n')}
                        </div>,
                        ...(block.paragraphs.length > 1
                            ? [
                                  block.paragraphs.map((paragraph, j) => {
                                      const paragraphId = `block-${i}-paragraph-${j}`;
                                      return (
                                          <div
                                              key={paragraphId}
                                              className="segmentItem"
                                              style={itemStyles(1, '#F4F4F4')}
                                          >
                                              {paragraph.words
                                                  .map((word) =>
                                                      word.symbols
                                                          .map((symbol) => symbol.text)
                                                          .join('')
                                                  )
                                                  .join(' ')}
                                          </div>
                                      );
                                  }),
                              ]
                            : []),
                    ];
                })}
            </div>
        </div>
    );
};
